import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Camera, Video, Upload, X, ArrowRight, Lock, Loader2, StopCircle, PlayCircle } from "lucide-react";
import { publicApi } from "@/lib/api";
import { ArrowDoodle, StarDoodle, CircleDoodle, UnderlineDoodle, HeartDoodle, ScribbleDoodle } from "@/components/Doodles";

const PROMPTS = [
  "What's your favourite memory with James?",
  "What's something you'll never let him forget?",
  "What has James taught you?",
  "What do you admire about him?",
  "Something he probably doesn't know you appreciate?",
  "Tell us a story.",
  "What was your first impression of him?",
  "Describe James in three words.",
  "What do you hope his next 40 years look like?",
  "Or just write whatever you want him to know.",
];

const RELATIONSHIP_PLACEHOLDERS = [
  "Mate since school",
  "Sister",
  "Work partner in crime",
  "Unfortunately related",
  "Old flatmate",
];

const CTA_HOVER_MS = 200;

export default function Home() {
  const [step, setStep] = useState("form"); // form | success
  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [message, setMessage] = useState("");
  const [threeWords, setThreeWords] = useState(["", "", ""]);
  const [photos, setPhotos] = useState([]); // { id, url, storage_path, name, uploading, progress, error }
  const [video, setVideo] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ctaHover, setCtaHover] = useState(false);
  const [promptIdx, setPromptIdx] = useState(0);
  const [relPh, setRelPh] = useState(RELATIONSHIP_PLACEHOLDERS[0]);
  const [deadline, setDeadline] = useState(null);

  const relPhIdx = useRef(0);

  // Rotating prompts
  useEffect(() => {
    const t = setInterval(() => setPromptIdx((i) => (i + 1) % PROMPTS.length), 4200);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const t = setInterval(() => {
      relPhIdx.current = (relPhIdx.current + 1) % RELATIONSHIP_PLACEHOLDERS.length;
      setRelPh(RELATIONSHIP_PLACEHOLDERS[relPhIdx.current]);
    }, 3000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    publicApi.get("/config/deadline").then((r) => setDeadline(r.data.deadline)).catch(() => {});
  }, []);

  async function ensureDraft() {
    if (submissionId) {
      // update in case name/message changed
      await publicApi.patch(`/submissions/${submissionId}`, {
        name, relationship, message, three_words: threeWords,
      });
      return submissionId;
    }
    if (!name.trim()) {
      toast.error("Pop your name in first — it's just for the vault.");
      throw new Error("no-name");
    }
    const res = await publicApi.post("/submissions", { name, relationship, message, three_words: threeWords });
    setSubmissionId(res.data.id);
    return res.data.id;
  }

  async function onSelectPhotos(files) {
    if (!files || !files.length) return;
    try {
      const sid = await ensureDraft();
      const arr = Array.from(files).slice(0, 10 - photos.length);
      for (const f of arr) {
        const tempId = crypto.randomUUID();
        const localUrl = URL.createObjectURL(f);
        setPhotos((p) => [...p, { id: tempId, url: localUrl, name: f.name, uploading: true, progress: 0 }]);
        const fd = new FormData();
        fd.append("file", f);
        try {
          const res = await publicApi.post(`/submissions/${sid}/photos`, fd, {
            onUploadProgress: (e) => {
              const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
              setPhotos((p) => p.map((x) => x.id === tempId ? { ...x, progress: pct } : x));
            },
          });
          setPhotos((p) => p.map((x) => x.id === tempId ? { ...x, uploading: false, progress: 100, storage_id: res.data.id, storage_path: res.data.storage_path } : x));
        } catch (err) {
          console.log(err);
          setPhotos((p) => p.map((x) => x.id === tempId ? { ...x, uploading: false, error: true } : x));
          toast.error(`Couldn't upload ${f.name}`);
        }
      }
    } catch (e) { /* handled */ }
  }

  async function removePhoto(p) {
    if (p.storage_id && submissionId) {
      try { await publicApi.delete(`/submissions/${submissionId}/photos/${p.storage_id}`); } catch (e) {}
    }
    setPhotos((arr) => arr.filter((x) => x.id !== p.id));
  }

  async function onVideoFile(file) {
    if (!file) return;
    try {
      const sid = await ensureDraft();
      const tempUrl = URL.createObjectURL(file);
      setVideo({ url: tempUrl, name: file.name, uploading: true, progress: 0 });
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await publicApi.post(`/submissions/${sid}/video`, fd, {
          onUploadProgress: (e) => {
            const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
            setVideo((v) => v ? { ...v, progress: pct } : v);
          },
        });
        setVideo((v) => ({ ...v, uploading: false, progress: 100, storage_id: res.data.id }));
      } catch (err) {
        toast.error("Video upload failed. Try a smaller file?");
        setVideo(null);
      }
    } catch (e) {}
  }

  async function submit() {
    if (!name.trim()) return toast.error("Add your name first.");
    if (!message.trim() && photos.length === 0 && !video) {
      return toast.error("Leave a little something — words, photo or video.");
    }
    if (photos.some((p) => p.uploading) || (video && video.uploading)) {
      return toast.error("Hold on — still uploading.");
    }
    setSubmitting(true);
    try {
      const sid = await ensureDraft();
      await publicApi.post(`/submissions/${sid}/finalize`);
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Couldn't send. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setStep("form");
    setName(""); setRelationship(""); setMessage(""); setThreeWords(["", "", ""]);
    setPhotos([]); setVideo(null); setSubmissionId(null);
    window.scrollTo({ top: 0 });
  }

  if (step === "success") return <SuccessScreen onAgain={reset} />;

  return (
    <div className="min-h-screen bg-cream text-ink relative overflow-x-clip" data-testid="home-page">
      <TopSecretBadge />
      <Hero />
      <ExplainerStrip />
      <StoriesSection />
      <FormSection
        name={name} setName={setName}
        relationship={relationship} setRelationship={setRelationship}
        relPh={relPh}
        message={message} setMessage={setMessage}
        promptIdx={promptIdx}
        photos={photos} onSelectPhotos={onSelectPhotos} removePhoto={removePhoto}
        video={video} onVideoFile={onVideoFile} setVideo={setVideo}
        threeWords={threeWords} setThreeWords={setThreeWords}
        deadline={deadline}
      />
      <SubmitSection
        submitting={submitting}
        onSubmit={submit}
        ctaHover={ctaHover}
        setCtaHover={setCtaHover}
      />
      <Footer />
    </div>
  );
}

function TopSecretBadge() {
  return (
    <div className="fixed top-4 right-4 md:top-6 md:right-8 z-40 rotate-[14deg] pointer-events-none select-none">
      <div className="starburst text-[10px] md:text-xs leading-[0.95]" data-testid="top-secret-badge">
        <span>TOP<br />SECRET*</span>
      </div>
      <div className="text-[10px] md:text-xs font-hand text-ink/70 mt-1 text-right">
        *well… sort of
      </div>
    </div>
  );
}

function Masthead() {
  return (
    <div className="border-b-4 border-ink bg-cream" data-testid="masthead">
      <div className="max-w-7xl mx-auto px-6 md:px-16 pt-6 pb-2">
        <div className="flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-ink/70">
          <span>Vol. 40 · Issue 01</span>
          <span className="hidden sm:inline">Fresh sends · fresh sends · fresh sends</span>
          <span>Est. 1986</span>
        </div>
        <div className="masthead-rule my-2" />
        <h1 className="hush-title text-center text-4xl md:text-7xl leading-none">
          THE HUSH HUSH TIMES
        </h1>
        <div className="masthead-rule my-2" />
        <div className="flex items-center justify-between text-[10px] tracking-[0.35em] uppercase text-ink/70">
          <span>James's 40th Issue</span>
          <span className="hidden sm:inline">Do not deliver to James</span>
          <span>One copy only</span>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative pt-24 md:pt-28 pb-16 md:pb-24 px-6 md:px-16 max-w-7xl mx-auto">
      <div className="uppercase tracking-[0.3em] text-[10px] md:text-xs text-ink/60 mb-6 md:mb-10 flex items-center gap-3">
        <span className="inline-block h-px w-8 bg-ink/40" />
        Operation: James Turns 40
      </div>

      <div className="grid md:grid-cols-12 gap-6 md:gap-10">
        <div className="md:col-span-8 relative">
          <div className="relative text-stamp">
            <div className="hush-title text-[3.5rem] sm:text-[5rem] md:text-[10rem]">
              <motion.span
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7 }}
                className="block"
              >
                keep this
              </motion.span>
              <motion.span
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.12 }}
                className="block italic relative"
              >
                hush hush.
                <span
                  className="script-overlay absolute left-[8%] top-[62%] md:top-[70%] -rotate-[8deg] text-3xl md:text-6xl pointer-events-none"
                  aria-hidden
                >
                  shhh…
                </span>
              </motion.span>
            </div>
            <StarDoodle className="absolute -top-4 right-2 w-10 h-10 md:w-14 md:h-14 -rotate-12 hidden sm:block" />
          </div>

          <div className="mt-8 md:mt-10 space-y-4 text-lg md:text-xl leading-relaxed text-ink/85 font-body max-w-xl">
            <p>
              We're putting together something special for James's 40th.
              And somehow, we need to keep it from him.
            </p>
            <p className="font-display font-black text-2xl md:text-3xl leading-tight text-ink">
              A little note. A favourite photo.<br />
              A story we've forgotten.<br />
              A video he'll probably cry watching.
            </p>
            <p>Whatever James means to you, we'd love you to add a little piece of it here.</p>

            <div className="pt-4 flex items-center gap-4">
              <a
                href="#your-turn"
                data-testid="add-yours-btn"
                className="inline-flex items-center gap-3 bg-ink text-cream rounded-full px-8 py-4 text-base md:text-lg font-medium hover:bg-ink/90 transition-transform hover:-translate-y-0.5"
              >
                Add yours <ArrowRight className="w-5 h-5" />
              </a>
              <ArrowDoodle className="w-16 h-10 -rotate-6 hidden sm:block animate-wiggle" />
            </div>
            <p className="font-hand text-2xl text-ink/70 pl-1">Seriously though… don't tell James.</p>
          </div>
        </div>

        {/* Property-of card sidebar */}
        <aside className="md:col-span-4 md:pl-6 relative">
          <div className="relative rotate-[-3deg] max-w-[300px] mx-auto md:mx-0 md:ml-auto scrapbook rounded-sm">
            <div className="aspect-[4/5] bg-creamdark overflow-hidden relative">
              <img
                src="/james.jpg"
                alt="James"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }}
              />
              <div className="absolute inset-0 hidden items-center justify-center bg-creamdark">
                <div className="text-center px-4">
                  <div className="text-[10px] tracking-[0.4em] text-ink/60">PROPERTY OF</div>
                  <div className="font-display font-black text-4xl mt-2">James</div>
                  <div className="text-[10px] tracking-[0.3em] text-ink/50 mt-3">DO NOT OPEN UNTIL 40</div>
                  <div className="mt-6 text-4xl">📸</div>
                </div>
              </div>
              <div className="absolute top-2 left-2 text-[10px] tracking-[0.3em] bg-cream/80 px-2 py-1 rotate-[-4deg]">PROPERTY OF JAMES</div>
            </div>
            <div className="text-center font-hand text-lg text-ink/70 pt-2">for your eyes only.</div>
          </div>
          <CircleDoodle className="absolute -top-4 -left-4 w-32 h-16 -rotate-6 hidden md:block" />
        </aside>
      </div>
    </section>
  );
}

function ExplainerStrip() {
  const items = [
    "Top Secret", "Evidence Accepted", "For his 40th", "Stories · Photos · Questionable Memories",
    "Classified", "Don't Tell James", "Straight to the Vault",
  ]; 
  const doubled = [...items, ...items];
  return (
    <div className="border-y-4 border-ink bg-lime overflow-hidden py-4">
      <div className="flex gap-10 whitespace-nowrap ticker-track font-display font-black italic text-xl md:text-2xl text-ink">
        {doubled.map((t, i) => (
          <span key={i} className="flex items-center gap-10">
            {t} <span className="text-stamp">✦</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function StoriesSection() {
  return (
    <section className="relative bg-bubblegum border-b-4 border-ink px-6 md:px-16 py-24 md:py-32 overflow-hidden">
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-stamp mb-4">— Feature Story —</div>
          <h2 className="hush-title text-6xl md:text-9xl text-stamp">
            40 years.<br />
            <span className="italic text-ink">a lot of</span><br />
            <span className="relative inline-block">
              stories.
              <span className="script-overlay absolute left-[6%] top-[55%] -rotate-[6deg] text-3xl md:text-5xl" aria-hidden>
                the good, the wild, the terrible
              </span>
            </span>
          </h2>
          <UnderlineDoodle className="w-56 h-4 mt-2 -rotate-1" />
        </div>
        <div className="md:col-span-5 md:pt-6 space-y-5 font-body text-lg text-ink/85 md:border-l-2 md:border-ink/70 md:pl-6">
          <div className="text-[10px] tracking-[0.35em] uppercase text-ink/70 mb-2">Editor's note</div>
          <p>
            Instead of trying to fit an entire person into one birthday card,
            we're collecting the people, stories, photos and moments that made
            the first 40 years what they were.
          </p>
          <p className="text-ink font-display font-black text-2xl">
            <span className="marker">You don't need to write anything perfect.</span>
          </p>
          <p className="font-hand text-2xl">Write like you're talking to him.</p>
        </div>
      </div>
    </section>
  );
}

function FormSection(props) {
  const {
    name, setName, relationship, setRelationship, relPh,
    message, setMessage, promptIdx,
    photos, onSelectPhotos, removePhoto,
    video, onVideoFile, setVideo,
    threeWords, setThreeWords, deadline,
  } = props;

  return (
    <section id="your-turn" className="px-6 md:px-16 max-w-4xl mx-auto pt-20 md:pt-28 pb-16" data-testid="form-section">
      <div className="relative">
        <div className="text-[10px] md:text-xs tracking-[0.4em] text-ink/60 mb-4">— YOUR TURN.</div>
        <h2 className="hush-title text-5xl md:text-7xl leading-[0.95] text-stamp">Leave something<br /><span className="italic text-ink">for James.</span></h2>
        <ScribbleDoodle className="absolute -right-4 -top-6 w-24 h-12 -rotate-6 hidden md:block" />
      </div>

      {deadline && (
        <p className="mt-8 text-base text-ink/70 font-hand text-2xl" data-testid="deadline-note">
          Please send yours in by <span className="marker">{new Date(deadline).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })}</span>.
        </p>
      )}

      {/* Name */}
      <div className="mt-16">
        <div className="text-xs tracking-[0.35em] text-ink/60 mb-4">01 · FIRST, WHO ARE YOU?</div>
        <input
          data-testid="name-input"
          className="field-underline text-3xl md:text-5xl"
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="mt-6">
          <label className="text-xs tracking-[0.3em] text-ink/50 font-hand text-xl block mb-2">
            How do you know James? <span className="text-ink/40 text-sm">(optional)</span>
          </label>
          <input
            data-testid="relationship-input"
            className="field-underline text-lg md:text-2xl"
            placeholder={relPh}
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
          />
        </div>
      </div>

      {/* Message */}
      <div className="mt-20">
        <div className="text-xs tracking-[0.35em] text-ink/60 mb-4">02 · SAY SOMETHING.</div>
        <p className="font-display text-2xl md:text-3xl italic leading-snug mb-4">
          Write a letter, a memory, a roast… <span className="font-hand not-italic text-ink/70">whatever feels like you.</span>
        </p>

        <div className="mb-4">
          <div className="text-[11px] tracking-[0.3em] text-ink/50">NEED A LITTLE INSPIRATION?</div>
          <div className="mt-2 h-14 relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={promptIdx}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="absolute inset-0 font-display text-xl md:text-2xl text-ink"
              >
                <span className="marker">{PROMPTS[promptIdx]}</span>
              </motion.div>
            </AnimatePresence>
          </div>
          <div className="text-xs text-ink/50 font-hand text-lg">You don't need to answer every prompt. Pick one, or skip them all.</div>
        </div>

        <textarea
          data-testid="message-input"
          rows={10}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Start writing…"
          className="w-full bg-transparent border-2 border-ink/80 rounded-2xl p-5 text-lg leading-relaxed focus:border-butterdark focus:outline-none placeholder:text-ink/30 font-body"
        />
      </div>

      {/* Photos */}
      <div className="mt-20">
        <div className="text-xs tracking-[0.35em] text-ink/60 mb-4">03 · SHOW US THE RECEIPTS.</div>
        <p className="font-display text-2xl md:text-3xl italic mb-4">Upload your favourite photos with James.</p>
        <p className="text-ink/70 mb-6">
          Old ones. New ones. Good ones. Terrible ones.
          <span className="font-hand text-xl block mt-1">Especially the terrible ones.</span>
        </p>

        <label
          data-testid="photo-dropzone"
          className="block cursor-pointer border-2 border-dashed border-ink/40 rounded-3xl p-10 text-center hover:bg-butter/40 transition-colors"
        >
          <Camera className="w-8 h-8 mx-auto mb-3" />
          <div className="font-display text-xl">Tap to add photos</div>
          <div className="text-sm text-ink/60 mt-1">JPG · PNG · HEIC · up to 10</div>
          <input
            data-testid="photo-input"
            type="file"
            accept="image/*,.heic,.heif"
            multiple
            className="hidden"
            onChange={(e) => onSelectPhotos(e.target.files)}
          />
        </label>

        {photos.length > 0 && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-6" data-testid="photo-previews">
            {photos.map((p, i) => (
              <div key={p.id} className={`relative scrapbook ${i % 3 === 0 ? "-rotate-2" : i % 3 === 1 ? "rotate-1" : "-rotate-1"}`}>
                <div className="aspect-square bg-creamdark overflow-hidden">
                  <img src={p.url} alt={p.name} className="w-full h-full object-cover" />
                </div>
                {p.uploading && (
                  <div className="absolute inset-0 bg-ink/50 flex items-center justify-center text-cream">
                    <div className="text-center">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                      <div className="mt-2 text-sm">{p.progress}%</div>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => removePhoto(p)}
                  className="absolute -top-3 -right-3 bg-ink text-cream rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:scale-110 transition"
                  data-testid={`remove-photo-${i}`}
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Video */}
      <div className="mt-20">
        <div className="text-xs tracking-[0.35em] text-ink/60 mb-4">04 · SAY IT TO CAMERA.</div>
        <p className="font-display text-2xl md:text-3xl italic mb-2">Record or upload a little video for James.</p>
        <p className="text-ink/70 mb-2">Heartfelt. Ridiculous. Ideally a little of both.</p>
        <p className="text-ink/85 mb-6 font-hand text-2xl">
          Even a quick "happy birthday" clip helps — we're making a surprise video separate from the written submissions.
        </p>

        <VideoUploader video={video} onVideoFile={onVideoFile} setVideo={setVideo} />
      </div>

      {/* Three words */}
      <div className="mt-20 bg-lavender text-cream border-4 border-ink rounded-3xl p-8 md:p-10">
        <div className="text-xs tracking-[0.35em] text-lime mb-2">ONE MORE THING… <span className="font-hand text-lg text-cream/70 normal-case tracking-normal">(optional)</span></div>
        <p className="font-display font-black text-2xl md:text-3xl">James in three words:</p>
        <div className="mt-5 grid grid-cols-3 gap-3 md:gap-6">
          {[0, 1, 2].map((i) => (
            <input
              key={i}
              data-testid={`three-word-${i}`}
              value={threeWords[i]}
              onChange={(e) => {
                const next = [...threeWords]; next[i] = e.target.value; setThreeWords(next);
              }}
              placeholder={["______", "______", "______"][i]}
              className="bg-transparent border-b-2 border-cream text-cream focus:border-lime focus:outline-none text-xl md:text-2xl text-center py-2 placeholder:text-cream/40"
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function VideoUploader({ video, onVideoFile, setVideo }) {
  const [recording, setRecording] = useState(false);
  const [recordSupported, setRecordSupported] = useState(true);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [recordTime, setRecordTime] = useState(0);
  const videoPreviewRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices || !window.MediaRecorder) {
      setRecordSupported(false);
    }
  }, []);

  async function startRecording() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.muted = true;
        videoPreviewRef.current.play();
      }
      const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ? "video/webm;codecs=vp9,opus"
        : MediaRecorder.isTypeSupported("video/webm") ? "video/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "video/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
        if (videoPreviewRef.current) videoPreviewRef.current.srcObject = null;
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      setRecordTime(0);
      timerRef.current = setInterval(() => setRecordTime((t) => t + 1), 1000);
    } catch (e) {
      toast.error("Couldn't access camera / mic.");
    }
  }

  function stopRecording() {
    if (recorderRef.current && recording) recorderRef.current.stop();
    setRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }

  function useRecorded() {
    if (!recordedBlob) return;
    const file = new File([recordedBlob], `james-video-${Date.now()}.webm`, { type: recordedBlob.type });
    onVideoFile(file);
    setRecordedBlob(null);
    if (recordedUrl) { URL.revokeObjectURL(recordedUrl); setRecordedUrl(null); }
  }

  function discardRecorded() {
    setRecordedBlob(null);
    if (recordedUrl) { URL.revokeObjectURL(recordedUrl); setRecordedUrl(null); }
  }

  if (video) {
    return (
      <div className="border border-ink/20 rounded-3xl p-6 bg-white" data-testid="video-preview">
        <div className="flex items-center gap-3 mb-4">
          <Video className="w-5 h-5" />
          <div className="flex-1 truncate font-medium">{video.name || "Your video"}</div>
          {!video.uploading && (
            <button
              onClick={() => setVideo(null)}
              className="text-sm underline text-ink/60 hover:text-ink"
              data-testid="remove-video"
            >Remove</button>
          )}
        </div>
        {video.uploading ? (
          <div>
            <div className="text-sm mb-2">Uploading… don't close this page.</div>
            <div className="h-2 bg-creamdark rounded-full overflow-hidden">
              <div className="h-full bg-butterdark transition-all" style={{ width: `${video.progress || 0}%` }} />
            </div>
            <div className="text-xs text-ink/60 mt-2">{video.progress || 0}%</div>
          </div>
        ) : (
          <div>
            <div className="text-sm text-ink/70 mb-3">✓ Video ready.</div>
            <video src={video.url} controls className="w-full rounded-xl bg-black" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="grid md:grid-cols-2 gap-4">
        <label
          className="cursor-pointer border-2 border-dashed border-ink/40 rounded-3xl p-8 text-center hover:bg-butter/40 transition-colors flex flex-col items-center justify-center"
          data-testid="video-upload-dropzone"
        >
          <Upload className="w-7 h-7 mb-2" />
          <div className="font-display text-xl">Upload a video</div>
          <div className="text-sm text-ink/60 mt-1">MP4, MOV, WebM · up to 300MB</div>
          <input
            data-testid="video-input"
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => onVideoFile(e.target.files?.[0])}
          />
        </label>

        {recordSupported ? (
          <div className="border-2 border-ink/40 rounded-3xl p-6 bg-white/60" data-testid="record-video-panel">
            <div className="flex items-center gap-2 text-xs tracking-[0.3em] text-ink/60"><Camera className="w-4 h-4" /> RECORD IN-BROWSER</div>
            {!recording && !recordedUrl && (
              <div className="mt-3 text-center">
                <div className="font-display text-xl mb-2">Say it to camera.</div>
                <p className="text-sm text-ink/70 mb-4 max-w-xs mx-auto leading-snug">
                  Even a quick <span className="marker">"happy birthday, James!"</span> is gold — we're stitching everyone's clips into a surprise birthday video.
                </p>
                <button
                  onClick={startRecording}
                  className="inline-flex items-center gap-2 bg-stamp text-cream rounded-full px-5 py-3 hover:opacity-90"
                  data-testid="start-recording-btn"
                >
                  <PlayCircle className="w-5 h-5" /> Start recording
                </button>
              </div>
            )}
            {recording && (
              <div className="mt-3">
                <div className="relative">
                  <video ref={videoPreviewRef} className="w-full rounded-xl bg-black aspect-video" />
                  <div className="absolute top-2 left-2 bg-stamp text-cream text-xs px-2 py-1 rounded-full font-mono">
                    ● REC {formatTime(recordTime)}
                  </div>
                </div>
                <button
                  onClick={stopRecording}
                  className="mt-3 inline-flex items-center gap-2 bg-ink text-cream rounded-full px-5 py-3"
                  data-testid="stop-recording-btn"
                >
                  <StopCircle className="w-5 h-5" /> Stop
                </button>
              </div>
            )}
            {!recording && recordedUrl && (
              <div className="mt-3">
                <video src={recordedUrl} controls className="w-full rounded-xl bg-black" />
                <div className="flex gap-2 mt-3">
                  <button onClick={useRecorded} className="flex-1 bg-ink text-cream rounded-full px-5 py-3" data-testid="use-recording-btn">Use this</button>
                  <button onClick={discardRecorded} className="rounded-full px-5 py-3 border border-ink/30">Redo</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="border-2 border-dashed border-ink/20 rounded-3xl p-8 text-center text-ink/50 text-sm">
            In-browser recording isn't supported on this device — upload works great though.
          </div>
        )}
      </div>
    </div>
  );
}

function formatTime(s) {
  const m = Math.floor(s / 60).toString().padStart(2, "0");
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

function SubmitSection({ submitting, onSubmit, ctaHover, setCtaHover }) {
  return (
    <section className="px-6 md:px-16 max-w-4xl mx-auto py-20 md:py-28" data-testid="submit-section">
      <div className="bg-ink text-cream border-4 border-ink rounded-3xl p-8 md:p-12">
        <div className="text-xs tracking-[0.35em] text-butter mb-3">ONE LAST THING…</div>
        <ul className="space-y-2 font-display font-black text-xl md:text-2xl">
          <li>✓ James can't see this</li>
          <li>✓ Your submission is private</li>
          <li>✓ Only the organiser can access it</li>
          <li>✓ Yes, we're really keeping this <span className="text-butter">hush hush</span></li>
        </ul>
      </div>

      <div className="mt-10 text-center">
        <button
          onClick={onSubmit}
          onMouseEnter={() => setCtaHover(true)}
          onMouseLeave={() => setCtaHover(false)}
          disabled={submitting}
          className="inline-flex items-center gap-3 bg-stamp hover:bg-stampdark disabled:opacity-70 text-cream rounded-full px-10 md:px-14 py-6 text-xl md:text-2xl font-display font-black shadow-[0_10px_0_0_#141210] hover:shadow-[0_6px_0_0_#141210] hover:translate-y-1 transition-all border-4 border-ink"
          data-testid="send-vault-btn"
        >
          {submitting ? <Loader2 className="w-6 h-6 animate-spin" /> : <Lock className="w-6 h-6" />}
          {submitting ? "Sending…" : (ctaHover ? "Shhh… send it →" : "Send it to the Vault →")}
        </button>
        <div className="mt-4 font-hand text-xl text-ink/70">James doesn't know this exists. Let's keep it that way.</div>
      </div>
    </section>
  );
}

function SuccessScreen({ onAgain }) {
  return (
    <section className="min-h-screen flex items-center justify-center px-6 py-24 bg-lime" data-testid="success-screen">
      <div className="max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ scale: 3, opacity: 0, rotate: -25 }}
          animate={{ scale: 1, opacity: 1, rotate: -8 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="inline-block mb-10"
        >
          <div className="stamp text-2xl md:text-3xl bg-cream/70">Classified · Until his 40th</div>
        </motion.div>

        <h1 className="hush-title text-6xl md:text-9xl mb-6 text-stamp relative inline-block">
          It's in the<br />
          <span className="italic">vault.</span>
          <span className="script-overlay absolute -right-4 md:-right-10 top-[45%] rotate-[-8deg] text-3xl md:text-6xl" aria-hidden style={{ color: "#826DEE" }}>you saw nothing.</span>
        </h1>
        <p className="font-display font-black text-2xl md:text-3xl text-ink mb-3 mt-6">
          Your piece of James history has officially been secured.
        </p>
        <p className="text-lg text-ink/85 mb-10">
          Thank you for helping make his 40th something very special.
        </p>

        <button
          onClick={onAgain}
          className="inline-flex items-center gap-3 bg-ink text-cream rounded-full px-8 py-4 hover:bg-ink/90 transition"
          data-testid="add-another-btn"
        >
          Add another memory <ArrowRight className="w-5 h-5" />
        </button>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-ink/15 mt-10 py-12 px-6 md:px-16 text-center text-sm text-ink/60">
      <div className="font-hand text-xl mb-2">Operation: James Turns 40 · a very small, very secret project.</div>
      <div>© {new Date().getFullYear()} · not affiliated with James, obviously.</div>
    </footer>
  );
}
